import { useEffect, useState, type ReactNode } from "react";
import { MenuIcon, ChevronLeftIcon, ChevronDownIcon } from "./icons";

/** Below this width the sidebar auto-collapses to the icon rail (like the Jmix drawer on small screens). */
const COLLAPSE_BREAKPOINT = "(max-width: 900px)";

export interface AppShellProps {
  /** Brand/app name shown next to the logo at the top of the sidebar. */
  brandTitle: string;
  /** Optional brand logo (url) rendered as the circular paw mark. */
  brandLogoSrc?: string;
  /** Current view title — the header h1. */
  title: string;
  /** Optional header slot to the right of the title (e.g. a global search field). */
  header?: ReactNode;
  /** Optional card pinned under the brand (e.g. the {@link LocationSelector} / vestiging card). */
  locationSelector?: ReactNode;
  /** Sidebar navigation — compose {@link NavSection} + {@link NavItem}. */
  nav: ReactNode;
  /** Optional pinned bottom-of-sidebar content (e.g. a {@link ProfileCard}). */
  sidebarFooter?: ReactNode;
  /** The working area. */
  children: ReactNode;
}

/**
 * jve-2-6: the application shell — left sidebar (brand · location · nav · footer) + top header
 * (drawer toggle · view title · header slot) + working area. Reproduces the Jmix `MainView`
 * AppLayout structure on the new design system (token-styled, theme-aware). The sidebar collapses
 * to an icon-only rail via the in-sidebar chevron or the header toggle. Extraction-ready.
 */
export function AppShell({
  brandTitle,
  brandLogoSrc,
  title,
  header,
  locationSelector,
  nav,
  sidebarFooter,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = () => setCollapsed((c) => !c);

  // Auto-collapse to the icon rail on small screens (the Jmix drawer collapses there too). A manual
  // toggle still works between viewport changes. Guarded for jsdom/SSR (no matchMedia).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(COLLAPSE_BREAKPOINT);
    const apply = () => setCollapsed(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return (
    <div className="fui-shell" data-collapsed={collapsed ? "" : undefined}>
      <aside className="fui-shell__sidebar">
        <div className="fui-shell__brand">
          {brandLogoSrc ? (
            <img className="fui-shell__brand-logo" src={brandLogoSrc} alt="" aria-hidden="true" />
          ) : (
            <span className="fui-shell__brand-logo fui-shell__brand-logo--placeholder" aria-hidden="true" />
          )}
          <span className="fui-shell__brand-title">{brandTitle}</span>
        </div>
        <button
          type="button"
          className="fui-shell__collapse-toggle"
          aria-label={collapsed ? "Navigatie uitklappen" : "Navigatie inklappen"}
          title={collapsed ? "Uitklappen" : "Inklappen"}
          onClick={toggle}
        >
          <ChevronLeftIcon width={16} height={16} />
        </button>
        {locationSelector && <div className="fui-shell__location">{locationSelector}</div>}
        <nav className="fui-shell__nav" aria-label={brandTitle}>
          {nav}
        </nav>
        {sidebarFooter && <div className="fui-shell__sidebar-footer">{sidebarFooter}</div>}
      </aside>
      <div className="fui-shell__main">
        <header className="fui-shell__header">
          <button
            type="button"
            className="fui-shell__drawer-toggle"
            aria-label="Navigatie tonen of verbergen"
            aria-expanded={!collapsed}
            onClick={toggle}
          >
            <MenuIcon width={22} height={22} />
          </button>
          <h1 className="fui-shell__title">{title}</h1>
          {header && <div className="fui-shell__header-slot">{header}</div>}
        </header>
        <main className="fui-shell__content">{children}</main>
      </div>
    </div>
  );
}

export interface NavSectionProps {
  /** Section label. Rendered as an uppercase static label, or a collapsible summary (see below). */
  label: string;
  /**
   * Header treatment, mirroring the Jmix sidebar groups:
   *   • omitted        → a static uppercase label, always open (the Jmix "APPLICATION" group);
   *   • `true`/`false` → a collapsible accordion (Title-case summary + leading chevron) whose
   *                      initial open state is this value — `menu.xml`'s `opened` flag.
   */
  defaultOpen?: boolean;
  /** Optional section glyph shown left of a collapsible summary (the Jmix Security/Data-Tools icon). */
  icon?: ReactNode;
  children: ReactNode;
}

/** A labelled group of {@link NavItem}s in the sidebar. */
export function NavSection({ label, defaultOpen, icon, children }: NavSectionProps) {
  const collapsible = defaultOpen !== undefined;
  const [open, setOpen] = useState(defaultOpen ?? true);

  if (!collapsible) {
    return (
      <div className="fui-nav-section">
        <div className="fui-nav-section__label">{label}</div>
        {children}
      </div>
    );
  }

  return (
    <div
      className="fui-nav-section"
      data-collapsible=""
      data-open={open ? "" : undefined}
      data-has-icon={icon ? "" : undefined}
    >
      <button
        type="button"
        className="fui-nav-section__summary"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDownIcon className="fui-nav-section__chevron" width={16} height={16} />
        {icon && (
          <span className="fui-nav-section__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="fui-nav-section__summary-text">{label}</span>
      </button>
      {/* Items stay in the DOM (display toggled by CSS) so the collapsed icon-rail can still show
          every glyph regardless of a section's open state. */}
      <div className="fui-nav-section__items">{children}</div>
    </div>
  );
}

export interface NavItemProps {
  label: string;
  active?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  /**
   * When set, the item renders as a link (full-page navigation) instead of a state-nav button — used
   * for not-yet-migrated screens that hand off to the legacy Jmix app at the site root.
   */
  href?: string;
  /** Marks a hand-off to the legacy Jmix app (adds a marker + tooltip). Pair with {@link href}. */
  legacy?: boolean;
  /** Tooltip override (defaults to the label; legacy items get a "opens in classic Backoffice" hint). */
  title?: string;
}

/**
 * A single sidebar navigation entry. Renders as a button for in-SPA (state-based) navigation, or as
 * an anchor when {@link href} is set (a real navigation — used to hand off to the legacy Jmix app).
 */
export function NavItem({ label, active, icon, onClick, href, legacy, title }: NavItemProps) {
  const inner = (
    <>
      {icon && (
        <span className="fui-nav-item__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="fui-nav-item__label">{label}</span>
    </>
  );

  if (href) {
    return (
      <a
        className="fui-nav-item"
        href={href}
        data-active={active ? "" : undefined}
        data-legacy={legacy ? "" : undefined}
        aria-current={active ? "page" : undefined}
        title={title ?? label}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      className="fui-nav-item"
      data-active={active ? "" : undefined}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      title={title ?? label}
    >
      {inner}
    </button>
  );
}
